import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getProgramList } from 'helpers/backend_helper';

interface Program {
  programId: number;
  programName: string;
}

interface ProgramState {
  // State
  programs: Program[];
  selectedProgram: Program | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchPrograms: () => Promise<void>;
  setSelectedProgram: (program: Program | null) => void;
  setPrograms: (programs: Program[]) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  programs: [],
  selectedProgram: null,
  isLoading: false,
  error: null,
};

export const useProgramStore = create<ProgramState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Actions
      fetchPrograms: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await getProgramList();
          
          if (response.data && Array.isArray(response.data)) {
            const programs = response.data.map((program: any) => ({
              programId: program.programId,
              programName: program.programName,
            }));
            
            set({ programs, isLoading: false });
            // Only auto-select if no program is currently selected
            if (get().selectedProgram === null) {
              set({ selectedProgram: programs[0] });
            }
          } else {
            set({ programs: [], isLoading: false });
          }
        } catch (error) {
          console.error('Error fetching programs:', error);
          set({ 
            error: 'Failed to fetch programs', 
            isLoading: false 
          });
        }
      },

      setSelectedProgram: (selectedProgram) => set({ selectedProgram }),

      setPrograms: (programs) => set({ programs }),

      clearError: () => set({ error: null }),

      reset: () => set(initialState),
    }),
    {
      name: 'program-storage',
      partialize: (state) => ({
        programs: state.programs,
        selectedProgram: state.selectedProgram,
      }),
    }
  )
);
